import React, { useState, useEffect } from "react";
import axios from "../../api/api";
import { Trash, Pencil } from "lucide-react";
import { toast } from "react-toastify";

export default function AddState() {
  const [states, setStates] = useState([]);
  const [countries, setCountries] = useState([]);
  const [newState, setNewState] = useState("");
  const [newCountry, setNewCountry] = useState("");
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [editingCountry, setEditingCountry] = useState("");

  useEffect(() => {
    fetchCountries();
    fetchStates();
  }, []);

  // Fetch Countries for dropdown
  const fetchCountries = async () => {
    try {
      const resp = await axios.get("/api/country");
      setCountries(resp.data?.data || []);
    } catch (err) {
      toast.error("Failed to load countries");
    }
  };

  // Fetch States
  const fetchStates = async () => {
    setLoading(true);
    try {
      const resp = await axios.get("/api/state");
      setStates(resp.data?.data || []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load states");
    } finally {
      setLoading(false);
    }
  };

  // Add State
  const handleAddState = async (e) => {
    e.preventDefault();

    const name = newState.trim();
    if (!name) {
      toast.error("State name cannot be empty");
      return;
    }
    if (!newCountry) {
      toast.error("Please select a country");
      return;
    }

    setLoading(true);
    try {
      await axios.post("/api/state/add-state", { name, country: newCountry });
      toast.success("State added successfully");
      setNewState("");
      setNewCountry("");
      fetchStates();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to add state");
    } finally {
      setLoading(false);
    }
  };

  // Delete State
  const handleDeleteState = async (id) => {
    if (!window.confirm("Are you sure you want to delete this state?")) return;

    setDeletingId(id);
    try {
      await axios.delete(`/api/state/${id}`);
      toast.success("State deleted successfully");
      setStates((prev) => prev.filter((s) => s._id !== id));
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to delete state");
    } finally {
      setDeletingId(null);
    }
  };

  // Start Edit
  const startEdit = (state) => {
    setEditingId(state._id);
    setEditingName(state.name);
    setEditingCountry(state.country?._id || state.country || "");
  };

  // Cancel Edit
  const cancelEdit = () => {
    setEditingId(null);
    setEditingName("");
    setEditingCountry("");
  };

  // Update State
  const handleUpdateState = async (id) => {
    const name = editingName.trim();
    if (!name) {
      toast.error("State name cannot be empty");
      return;
    }
    if (!editingCountry) {
      toast.error("Please select a country");
      return;
    }

    try {
      await axios.put(`/api/state/${id}`, { name, country: editingCountry });
      toast.success("State updated successfully");

      setStates((prev) =>
        prev.map((s) =>
          s._id === id
            ? {
                ...s,
                name,
                country: countries.find((c) => c._id === editingCountry) || s.country,
              }
            : s
        )
      );

      cancelEdit();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update state");
    }
  };

  return (
    <div className="flex flex-1 bg-gray-100 dark:bg-gray-900">
      <div className="page-wrapper relative ml-auto w-[calc(100%-260px)] px-6 pt-24">
        <div className="xl:w-full min-h-[calc(100vh-152px)] pb-14">
          <div className="grid xl:grid-cols-12 gap-6">
            <div className="xl:col-span-6 xl:col-start-4">
              <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-md">

                {/* Header */}
                <div className="border-b border-gray-200 dark:border-gray-700 py-4 px-6 flex justify-between items-center">
                  <h4 className="font-semibold text-xl text-gray-800 dark:text-gray-200">
                    Manage States
                  </h4>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    Total: {states.length}
                  </span>
                </div>

                {/* Body */}
                <form onSubmit={handleAddState} className="p-6">

                  {/* Country Dropdown */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Select Country
                    </label>
                    <select
                      value={newCountry}
                      onChange={(e) => setNewCountry(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-transparent focus:outline-none focus:ring-2 focus:ring-primary-500 text-gray-800 dark:text-gray-200"
                    >
                      <option value="">Select Country</option>
                      {countries.map((c) => (
                        <option key={c._id} value={c._id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* State Name */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Add New State
                    </label>
                    <input
                      type="text"
                      value={newState}
                      onChange={(e) => setNewState(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-transparent focus:outline-none focus:ring-2 focus:ring-primary-500 text-gray-800 dark:text-gray-200"
                      placeholder="Enter state name"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="px-4 py-2 bg-primary-500 text-white rounded-md hover:bg-primary-600 disabled:opacity-50"
                  >
                    {loading ? "Adding..." : "Add State"}
                  </button>

                  {/* State List */}
                  <div className="mt-8">
                    <h5 className="font-semibold text-lg text-gray-800 dark:text-gray-200 mb-3">
                      Existing States
                    </h5>

                    {loading ? (
                      <div className="space-y-3">
                        {[...Array(3)].map((_, i) => (
                          <div
                            key={i}
                            className="h-12 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"
                          />
                        ))}
                      </div>
                    ) : states.length === 0 ? (
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        No states found
                      </p>
                    ) : (
                      <div className="space-y-3">
                        {states.map((state) => (
                          <div
                            key={state._id}
                            className="flex items-center justify-between px-4 py-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 hover:shadow transition"
                          >
                            {editingId === state._id ? (
                              /* ── Edit Mode ── */
                              <div className="flex flex-col gap-2 w-full mr-3">
                                <select
                                  value={editingCountry}
                                  onChange={(e) => setEditingCountry(e.target.value)}
                                  className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md bg-transparent text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-0"
                                >
                                  <option value="">Select Country</option>
                                  {countries.map((c) => (
                                    <option key={c._id} value={c._id}>
                                      {c.name}
                                    </option>
                                  ))}
                                </select>
                                <input
                                  value={editingName}
                                  onChange={(e) => setEditingName(e.target.value)}
                                  className="px-2 py-1 border-0 outline-none focus:outline-none focus:ring-0 text-gray-800 dark:text-gray-200"
                                />
                              </div>
                            ) : (
                              /* ── View Mode ── */
                              <div className="flex flex-col">
                                <span className="font-medium text-gray-800 dark:text-gray-200">
                                  {state.name}
                                </span>
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  {state.country?.name || "—"}
                                </span>
                              </div>
                            )}

                            <div className="flex items-center gap-2 shrink-0">
                              {editingId === state._id ? (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => handleUpdateState(state._id)}
                                    className="text-green-600 text-sm"
                                  >
                                    Save
                                  </button>
                                  <button
                                    type="button"
                                    onClick={cancelEdit}
                                    className="text-gray-500 text-sm"
                                  >
                                    Cancel
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => startEdit(state)}
                                    className="p-2 text-blue-600"
                                  >
                                    <Pencil size={16} />
                                  </button>
                                  <button
                                    type="button"
                                    disabled={deletingId === state._id}
                                    onClick={() => handleDeleteState(state._id)}
                                    className="p-2 text-red-600 disabled:opacity-50"
                                  >
                                    <Trash size={16} />
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}